export type CreatorFormBaseParams = {
  requirementType: EventTaskRequirementType
}

export type CreatorFormActivityParams = CreatorFormBaseParams & {
  activityId: Guid
}

export type CreatorFormReadingUrlParams = CreatorFormBaseParams & {
  url: string
}

export type CreatorFormReadingPdfParams = CreatorFormBaseParams & {
  files: Nullable<File[]>
}

export type CreatorFormAssessmentCriteriaParams = {
  testCriteriaId1: string
  testCriteriaId2?: string
  testCriteriaId3?: string
}

export type CreatorFormAssessmentParams = CreatorFormBaseParams & CreatorFormAssessmentSelectParams

export type CreatorFormAssessmentSelectParams = {
  cesTestType?: CesTestType
  departmentId?: Guid
  responsibilityLevelId?: Guid
  testDefinitionId?: Guid
  vesselTypeId?: Guid
  detailedSubjectId?: Guid
}

type VirtualEventTaskBaseParams = {
  id: Guid
  eventId: Guid
  requirementId: Nullable<string>
  title: string
  url?: string
  isRequired: boolean
  isPostEvent: boolean
  taskType: VirtualEventTaskType
}

export type VirtualEventTask = VirtualEventTaskBaseParams & {
  assessmentType?: CesTestType
}

export type Assessment = {
  testType: CesTestType
  departmentId: Guid
  levelId: Guid // responsibilityLevelId
  testId: Guid // testDefinitionId
  vesselTypeId: Guid
  detailedSubjectId: Guid
}

export type GetVirtualEventTask = VirtualEventTaskBaseParams & {
  assessment: Assessment
}

export enum CesTestType {
  Stcw = 1,
  Detailed = 3,
  CompanySpecific = 4,
}

export enum VirtualEventTaskType {
  Url, // Reading (URL)
  File, // Reading (PDF)
  Survey, // Pulse survey
  RapidElearning,
  Elearning,
  Assessment,
}

export enum EventTaskRequirementType {
  Required,
  Recommended,
}

export type RequirementTaskProperties = {
  requirementId: Guid
}

export type AssessmentTaskProperties = {
  cesTestType: CesTestType
  testCriteriaId1?: string
  testCriteriaId2?: string
  testCriteriaId3?: string
}

export type UrlTaskProperties = {
  url: string
}

export type AddVirtualEventTaskModel = {
  eventId: Guid
  isPostEvent: boolean
  isRequired: boolean
  taskType: VirtualEventTaskType
  urlProperties?: UrlTaskProperties
  requirementProperties?: RequirementTaskProperties
  assessmentProperties?: AssessmentTaskProperties
}

export type AddVirtualEventTaskWithFileModel = {
  eventId: Guid
  isPostEvent: boolean
  isRequired: boolean
  file: File
}

export type UpdateVirtualEventTaskModel = {
  taskId: Guid
  isRequired: boolean
  urlProperties?: UrlTaskProperties
  requirementProperties?: RequirementTaskProperties
  assessmentProperties?: AssessmentTaskProperties
}

export enum Availability {
  NotAvailable,
  Before,
  After,
  BeforeAndAfter,
}

export type AvailabilityOption = { value: Availability; label: string }

export type PresentationFile = {
  id: Guid
  name: string
  size: number
  eventId: Guid
  availability: Availability
}

export type RapidElearning = {
  id: Guid
  title: string
  number: string
  sortCode: number
  isActive: boolean
  chaptersNumber: number
}

export enum ElearningTypes {
  TrainingModule,
  PocketGuide,
  TrainingVideo,
}

export type Elearning = {
  id: Guid
  type: ElearningTypes
  discName: string
  discRelease: number
  moduleName: string
  numberOfChapters: number
  sortCode: number
  isActive: boolean
  duration: number
  categoryIds: string[]
  releaseDate: DateTime
  numberOfReleases: number
  targetGroups: string[]
  imageTimeStamp: string
}

export enum SurveyAvailabilityTypes {
  All,
  Online,
  Vessel,
}

export type DistributionChannelDetails = {
  trainingLibrary: boolean
  systemNotification: boolean
  highlightNotificationPopup: boolean
  personnelEmail: boolean
}

export type Survey = {
  id: Guid
  title: string
  number: string
  sortCode: number
  isActive: boolean
  numberOfQuestions: number
  isCompanySpecific: boolean
  distributionChannelDetails: DistributionChannelDetails
  availabilityRule: SurveyAvailabilityTypes
}

export type CrewEvaluationReferenceBaseParams = {
  id: Guid
  isActive: boolean
  name: string
  sortCode: number
}

// TODO: Do we need correct typing here?
export enum DepartmentEntryType {
  Department,
  FunctionArea,
}

export type Department = CrewEvaluationReferenceBaseParams & {
  entryType: DepartmentEntryType
}

export type ResponsibilityLevel = CrewEvaluationReferenceBaseParams

export enum CrewEvaluationDefinitionStatus {
  Modified = 1,
  Published = 2,
  Archived = 3,
}

export enum AlternativeFeedbackType {
  IndicateTheExactNumberOfCorrectAlternatives,
  IndicateIfThereIsOneOrMultipleCorrectAlternatives,
  GiveNoIndicationOfNumberOfCorrectAlternatives,
}

export type TestDefinition = Pick<CrewEvaluationReferenceBaseParams, 'id' | 'name'> & {
  author: string
  averageTimeUsedPerQuestion: number
  completionText: string
  feedbackType: AlternativeFeedbackType
  includeDontKnowAnswer: boolean
  introductionText: string
  isAvailableInSubOffice: boolean
  isAvailableOnVessel: boolean
  isAvailableOnline: boolean
  lastEdited: DateTime
  revisionNumber: number
  showGroupName: boolean
  showGroupsRandomly: boolean
  showIntroductionText: boolean
  showQuestionId: boolean
  status: CrewEvaluationDefinitionStatus
}

export type TestType = Pick<CrewEvaluationReferenceBaseParams, 'id' | 'name' | 'sortCode'> & {
  canBeRun: boolean
  characterTestTypeId: Identifier
  legacyTestTypeId: number
}

export enum VesselEntryType {
  Vessel,
  Propulsion,
}

export type VesselType = CrewEvaluationReferenceBaseParams & {
  entryType: VesselEntryType
}

export type DetailedSubject = CrewEvaluationReferenceBaseParams & {
  isVisible: boolean
}

export type StcwTestCriterion = Pick<CrewEvaluationReferenceBaseParams, 'id'> & {
  departmentId: Guid
  functionId: Guid
  responsibilityLevelId: Guid
  vesselTypeId: Guid
}

export type DetailedTestCriterion = Pick<CrewEvaluationReferenceBaseParams, 'id'> & {
  departmentId: Guid
  functionId: Guid
  detailedSubjectId: Guid
}

export type CrewEvaluationReferences = {
  departments: Department[]
  responsibilityLevels: ResponsibilityLevel[]
  testTypes: TestType[]
  vesselTypes: VesselType[]
  detailedSubjects: DetailedSubject[]
}

export type CrewEvaluationTestDefinitions = {
  testDefinitions: TestDefinition[]
}

export type CrewEvaluationStcwTestCriteria = {
  testCriteria: StcwTestCriterion[]
}

export type CrewEvaluationDetailedTestCriteria = {
  testCriteria: DetailedTestCriterion[]
}

export type GetVirtualEventTaskResponse = {
  task: GetVirtualEventTask
}

export type ListVirtualEventPresentationFilesResponse = {
  presentationFiles: PresentationFile[]
}

export type VirtualEventTaskList = {
  tasks: VirtualEventTask[]
}

export type SurveysList = {
  surveys: Survey[]
}

export type ElearningList = {
  modules: Elearning[]
}

export type RapidElearningList = {
  rapidElearnings: RapidElearning[]
}
